import { useCallback, useEffect, useState } from 'react';
import { useFlag } from '@openfeature/react-sdk';
import { FEATURE_FLAGS } from '../../../../constants/constants';
import useNRMutation from '../../../../graphql/UseNRMutation';
import useNRQuery from '../../../../graphql/useNRQuery';
import { GET_JIRA_INTEGRATION_SETUP, QL_JIRA_INTEGRATION_SETUP_ACTION } from '../../../../graphql/queries/jiraIntegration';
import NRJiraReconnectModal from './NRJiraReconnectModal';
import { JIRA_SETUP_ACTION, JIRA_SETUP_MODE } from './jiraSetup.constants';

function openExternalUrl(url) {
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

function NRJiraReconnectModalContent({ onClose, onComplete }) {
  const { value: isJiraIntegrationEnabled } = useFlag(FEATURE_FLAGS.SETTINGS_JIRA_INTEGRATION, false);
  const [reconnectData] = useNRQuery({
    query: GET_JIRA_INTEGRATION_SETUP,
    options: {
      fetchPolicy: 'network-only',
      variables: { args: { mode: JIRA_SETUP_MODE.RECONNECT } }
    },
    qlObjKey: 'jiraIntegrationSetup',
    defaultValue: null
  });

  const [runSetupAction, setup, isSubmitting, setupError] = useNRMutation(
    QL_JIRA_INTEGRATION_SETUP_ACTION,
    'jiraIntegrationSetupAction'
  );
  const [pendingReconnect, setPendingReconnect] = useState(false);

  useEffect(() => {
    if (!pendingReconnect || isSubmitting) {
      return;
    }

    if (setupError) {
      setPendingReconnect(false);
      return;
    }

    // Wait until the START_RECONNECT mutation result is available.
    if (!setup) {
      return;
    }

    setPendingReconnect(false);
    openExternalUrl(setup.primaryAction?.url);
    runSetupAction({
      variables: {
        args: {
          action: JIRA_SETUP_ACTION.START_AUTHORIZATION
        }
      }
    });
    onComplete?.();
    onClose?.();
  }, [pendingReconnect, isSubmitting, setup, setupError, runSetupAction, onComplete, onClose]);

  const handleReauthorize = useCallback(() => {
    setPendingReconnect(true);
    runSetupAction({
      variables: {
        args: {
          action: JIRA_SETUP_ACTION.START_RECONNECT
        }
      }
    });
  }, [runSetupAction]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  if (!isJiraIntegrationEnabled) {
    return null;
  }

  return (
    <NRJiraReconnectModal
      open
      instanceUrl={reconnectData?.instanceUrl}
      connectedSpacesCount={reconnectData?.reconnectSummary?.connectedSpacesCount}
      onClose={handleClose}
      onCancel={handleClose}
      onReauthorize={handleReauthorize}
      isSubmitting={isSubmitting || pendingReconnect}
    />
  );
}

function NRJiraReconnectModalContainer({ open, onClose, onComplete }) {
  if (!open) {
    return null;
  }

  return <NRJiraReconnectModalContent onClose={onClose} onComplete={onComplete} />;
}

export default NRJiraReconnectModalContainer;
